// A segment is one row of the archive: a show, or a jukebox track between two of them.

export type SegmentKind = "show" | "track";
export type SegmentState = "provisional" | "approved" | "exported";
export type AudioState = "buffer" | "archived" | "tombstone";

/**
 * Artist and title off a raw stream title, mirroring `worker.titles.parse_track_title`.
 *
 * For captioning a jukebox track before the segmenter has written its row: until then the raw
 * title is all there is, and it carries the artist in front of the track. An unsplittable title
 * stays whole as the title, not the artist -- same rule as the worker's. Not for a live show,
 * whose raw title is a billing and a place rather than "artist - track".
 */
export function splitRawTitle(raw: string): { artist: string | null; title: string } {
  const match = /\s+[-–—]\s+/.exec(raw);
  if (!match) return { artist: null, title: raw };
  return { artist: raw.slice(0, match.index), title: raw.slice(match.index + match[0].length) };
}

export interface TimelineGap {
  start: string;
  end: string;
  seconds: number;
}

export interface Publication {
  target: string;
  state: "pending" | "posted" | "failed";
  url: string | null;
  /** What was left out, when something was. */
  note: string | null;
  error: string | null;
  posted_at: string | null;
}

export interface PublishStatus {
  name: string;
  label: string;
  enabled: boolean;
  /** Why it cannot post, or null when it can. */
  blocker: string | null;
  /** Who it is signed in as. Null when signed out or the credential was rejected. */
  account: string | null;
}

export interface Segment {
  id: number;
  kind: SegmentKind;
  start: string;
  end: string;
  seconds: number;
  /**
   * Stretches inside [start, end) that are not this show's audio: what played while a
   * dropped connection cut it in half, skipped when the halves were joined. `seconds`
   * already excludes them.
   */
  excisions: TimelineGap[];
  /**
   * The first section of the joined show this row continues, or null when it stands alone or
   * is itself the first section.
   *
   * A show cut in half by a dropped connection stays two rows: both are drawn on the
   * timeline exactly as they aired, and the jukebox track between them is still there to be
   * clicked. Playing the first section is what plays the whole show.
   */
  join_head_id: number | null;
  /**
   * The whole joined show's playing time, on its first section and null everywhere else --
   * so a non-null value also means "this row heads a group", which is what decides whether
   * unjoining it means anything. The stream list shows this in place of `seconds`.
   */
  join_seconds: number | null;
  detected_start: string;
  detected_end: string;
  edited: boolean;
  /** An operator named this row by hand, so the worker no longer re-parses over it. */
  metadata_edited: boolean;
  title: string | null;
  djs: string | null;
  guests: string | null;
  /** A track's performer. Null on a show, as `djs` is on a track. */
  artist: string | null;
  /** "LIVE from", "LIVE PARTY from", "RECORDED at" — the station's own claim. */
  marker: string | null;
  city: string | null;
  /** State or province, when the country has one: the "OH" of "Columbus, OH, USA". */
  region: string | null;
  country: string | null;
  /** `region` and `country` rejoined, as the schedule writes them. Derived server-side. */
  country_with_state: string | null;
  raw_title: string | null;
  state: SegmentState;
  audio_state: AudioState;
  starred: boolean;
  playable: boolean;
  /**
   * The filename this row's audio and transcript download as, without an extension:
   * datetime, billing, show and place, e.g. `2026-07-29_1000_nucleus_deep-sessions_bristol-uk`.
   *
   * Derived server-side, so the saved file, the URL it came from and the copy on the
   * archive disk all spell it identically.
   */
  download_slug: string;
  /**
   * The short archive link, `/dl/{id}/{dj}/{series}` -- what the download button points at and
   * what a VK or Telegram post prints. The saved file is still named by `download_slug`,
   * which the server sends in Content-Disposition.
   */
  download_path: string;
  /**
   * Why the archived copy cannot be dropped and cut again, or null when it can.
   *
   * The server's own answer, so the admin UI greys the action out instead of an operator
   * discovering the refusal by being told no. Always null on list responses, which do not
   * pay for the file stats behind it.
   */
  unarchive_blocker: string | null;
  /** Still running. Its end moves on every segmentation pass until it stops. */
  on_air: boolean;
  event_id: number | null;
  show_id: number | null;
  match_score: number | null;
  /** The show this track was fingerprint-matched inside. Null on anything else. */
  matched_in_id: number | null;
  auto_approved: boolean;
  /** The chosen cover's id. `artwork_url` is this, rendered; the picker needs the id. */
  artwork_id: number | null;
  /** What a pin on a track scopes to. Null on a show, and on an unrecognised track. */
  track_identity_id: number | null;
  /**
   * Whether this row's series, billing or track identity has a pin recorded against it.
   *
   * Only a stored decision can be withdrawn -- airings that merely share a hand-set cover
   * were never pinned -- so this is what decides whether the inspector offers to. Always
   * null on list responses, which do not pay for the lookup.
   */
  artwork_pinned: boolean | null;
  artwork_url: string | null;
  /**
   * What the signed-in account has heard of this show: 0 not at all, 1-99 partly, 100
   * listened to. Null when signed out, and on every track -- the tracker is a show thing.
   */
  listened_pct: number | null;
  /** What has been posted where. Always null on list responses. */
  posts: Publication[] | null;
  /** Why this row cannot be posted, or null when it can. Always null on list responses. */
  publish_blocker: string | null;
}

export interface SegmentPage {
  items: Segment[];
  total: number;
  limit: number;
  offset: number;
}

/** One record the fingerprinter believes was played inside a show. */
export interface TrackDetection {
  id: number;
  track_identity_id: number;
  artist: string | null;
  title: string | null;
  start: string;
  end: string;
  /**
   * Panako's own number: matching hashes summed over the twenty-second windows the play was
   * welded out of. It has no ceiling and it grows with the length of the record, so it is a
   * comparison between the candidates for one stretch and not a percentage of anything.
   */
  score: number;
  /** A ratio against the record as pressed: 1.062 means the DJ ran it 6.2% fast. */
  time_factor: number;
  /** The TRACK segment this became, or null while it sits below the promotion threshold. */
  promoted_segment_id: number | null;
}
